import { useEffect, useState } from "react";
import {
  dispatchVariantChangedAnalyticsEvent,
  dispatchPageLoadAnalyticsEvent,
} from "../../utils/analytics/analytics";
import { Variant, Product, PageConfig, FeatureFlags } from "../../types/types";
import { setCustomAttribute } from "../../utils/newrelic/requests";

type Props = {
  product: Product;
  selectedVariant: Variant;
  pageConfig: PageConfig;
  shouldFireGoogleEvents?: boolean;
  flags?: FeatureFlags;
};

function dispatchVariantChangedEvent(
  type: string,
  selectedVariant: Variant,
  product: Product,
) {
  dispatchVariantChangedAnalyticsEvent(type, selectedVariant, product);

  document.dispatchEvent(
    new CustomEvent("jl-events.browser.productChanged", {
      detail: {
        productCode: selectedVariant.displayCode,
        productName: selectedVariant.title,
        isSkuAvailableOnline:
          selectedVariant.availability?.availableToOrder || "",
        skuId: selectedVariant.aliases.skuId,
        skuPrice: selectedVariant.price?.value || "",
        changeType: type,
      },
    }),
  );
}

export function usePageLevelEvents({
  product,
  selectedVariant,
  pageConfig,
  flags,
}: Props) {
  const [differentiators, setDifferentiators] = useState(
    selectedVariant.differentiators,
  );
  // pdp-scaffold-google-analytics-poc

  useEffect(() => {
    if (differentiators?.colour !== selectedVariant?.differentiators?.colour) {
      dispatchVariantChangedEvent("colour", selectedVariant, product);
      setDifferentiators(selectedVariant.differentiators);
      return;
    }
    if (differentiators?.size !== selectedVariant?.differentiators?.size) {
      dispatchVariantChangedEvent("size", selectedVariant, product);
      setDifferentiators(selectedVariant.differentiators);
      return;
    }

    // These will all only be called if colour and size haven't changed,
    // which should mean only on page load.
    dispatchPageLoadAnalyticsEvent(product, selectedVariant, pageConfig.name);
  }, [product, selectedVariant]);

  useEffect(() => {
    setCustomAttribute("pageConfig", `${pageConfig.name}`);
    setCustomAttribute(
      "pageConfigSelectionMethod",
      `${pageConfig.selectionMethod}`,
    );
    setCustomAttribute("variantCount", `${product.variants.length}`);
  }, [pageConfig]);

  useEffect(() => {
    if (flags?.usePrefetching?.value) {
      setCustomAttribute("prefetchingEnabled", "true");
    }
  }, [flags]);
}
